import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { BRAND } from "../data/content";

// Tiny TikTok glyph (lucide doesn't ship one). Inherits color via currentColor.
const TikTokIcon = ({ className = "h-4 w-4" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-brand-charcoal text-white pt-16 sm:pt-20 pb-12 grain"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Brand */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <img
                src={BRAND.logo}
                alt="Fresh Fast Foods LLC logo"
                className="h-12 w-12 object-contain"
              />
              <div>
                <p className="font-display font-extrabold text-[17px] leading-tight tracking-tight">
                  Fresh Fast Foods LLC
                </p>
                <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand-gold">
                  A Food Service Company
                </p>
              </div>
            </div>
            <p className="mt-5 text-[14px] text-white/65 leading-relaxed max-w-md">
              A food service company built on quality food, professional service, and
              community impact. Quality food starts with quality people. Powered by
              BTWNYC quick-service operations.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={BRAND.social.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label={`Instagram ${BRAND.social.instagramHandle}`}
                title={`Instagram ${BRAND.social.instagramHandle}`}
                data-testid="footer-instagram"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06] border border-white/10 text-white hover:bg-brand-teal hover:border-brand-teal transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={BRAND.social.tiktok}
                target="_blank"
                rel="noreferrer"
                aria-label={`TikTok ${BRAND.social.tiktokHandle}`}
                title={`TikTok ${BRAND.social.tiktokHandle}`}
                data-testid="footer-tiktok"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06] border border-white/10 text-white hover:bg-brand-teal hover:border-brand-teal transition-colors"
              >
                <TikTokIcon className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="lg:col-span-5 lg:max-w-xs">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold">
              Contact
            </p>
            <ul className="mt-5 space-y-3.5">
              <li>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid="footer-phone"
                  className="group inline-flex items-start gap-3 text-[14px] text-white/85 hover:text-white"
                >
                  <Phone className="h-4 w-4 mt-0.5 text-brand-teal group-hover:text-brand-gold transition-colors" />
                  {BRAND.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="footer-email"
                  className="group inline-flex items-start gap-3 text-[14px] text-white/85 hover:text-white"
                >
                  <Mail className="h-4 w-4 mt-0.5 text-brand-teal group-hover:text-brand-gold transition-colors" />
                  {BRAND.email}
                </a>
              </li>
              <li className="inline-flex items-start gap-3 text-[14px] text-white/85" data-testid="footer-pickup-block">
                <MapPin className="h-4 w-4 mt-0.5 text-brand-teal" />
                <span>
                  <span className="block">{BRAND.serviceArea}</span>
                  <span
                    data-testid="footer-pickup-address"
                    className="block text-white/55 text-[12px] mt-0.5"
                  >
                    Pickup: 110-46 Merrick Blvd, Jamaica, NY 11433
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[12px] text-white/55">
            © 2026 Fresh Fast Foods LLC. All Rights Reserved.
          </p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/45">
            Built in Queens · Served Across NYC
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
